/**
 * Costanti di configurazione per OpportunityForm (App.js).
 *
 * Estratte da App.js il 25/02/2026 nell'ambito del refactoring del
 * componente monolitico (>2000 righe). Queste costanti sono pure data,
 * non dipendono dallo stato del componente e possono essere importate
 * in qualunque punto della codebase senza side effects.
 */

// Prestazioni visibili SOLO nelle pipeline "Indiretti" (Reparto Clinico /
// Ortodonzia). Vengono filtrate dalla lista per tutte le altre pipeline.
// Match per codice (insensible a maiuscole). Aggiungerne qui altri se in
// futuro servono.
export const INDIRETTI_ONLY_TREATMENT_CODES = new Set(["PVCI"]);

export const INDIRETTI_REPARTI = new Set(["indiretti_clinico", "indiretti_ortodonzia"]);

// Stage assicurativi in cui è permesso lo split-Indiretti anche se l'opp
// è in "stage-lock" (i.e. è uscita dal primo stage editabile). Richiesta
// utente 04/06/2026: voglio poter spostare prestazioni in Indiretti
// anche da opp che sono già state autorizzate o caricate, mantenendo
// readonly tutto il resto del piano (foto/medico/categorie/...).
// Tenere allineato a `_FINAL_STATUSES` / categorizzazione stage in
// routes_dashboard.
// eslint-disable-next-line no-unused-vars
export const STAGES_WHITELIST_INDIRETTI_SPLIT = new Set([
  "da autorizzare",
  "caricati",
  "preventivi da caricare",
  "preventivi caricati",
  "caricato da paziente",
  "caricati da paziente",
]);

// Map each "reparto" to the GHL pipeline/stage configuration.
// - When `needsStageSelection: true`, the UI shows a live list of stages
//   fetched from GHL so the user can pick which stage to drop the
//   opportunity into.
// - Otherwise the reparto uses its fixed `stageId` (empty = GHL's default
//   first stage for that pipeline).
export const REPARTI_CONFIG = {
  reparto_clinico: {
    // New pipeline "Assicurazioni - Reparto Clinico - 01" (Feb 2026 migration)
    pipelineId: "529iRmd3Wh7bjdgfHpP9",
    stageId: "f5d4499c-a799-4096-8272-41b6a27a0699", // "Da autorizzare"
  },
  reparto_clinico_completati: {
    pipelineId: "529iRmd3Wh7bjdgfHpP9",
    stageId: "c867f3cb-2647-4500-9e0f-62527541aec2", // "Caricati da paziente"
  },
  indiretti_clinico: {
    // Pipeline "Indiretti - Reparto Clinico" — pazienti senza assicurazione
    pipelineId: "he2K2EvoAaa3SxaikphM",
    stageId: "ace58aa1-4e66-460d-b8b1-e306a422b76a", // "New Lead"
  },
  promemoria_appuntamento: {
    // New pipeline "Promemoria - Reparto Clinico 01" (May 2026 migration)
    pipelineId: "I2wcBQKe3y6DhTYfFySx",
    stageId: "",
    needsStageSelection: true,
  },
  ortodonzia: {
    // New pipeline "Assicurazioni - Ortodonzia - 01" (Feb 2026 migration)
    pipelineId: "Eu6Xz0GLvHfN5PejJ4SV",
    stageId: "19f2bfd5-081c-44bc-b5b4-d2b0415c0e26", // "Preventivi da caricare"
  },
  indiretti_ortodonzia: {
    // Pipeline "Indiretti - Ortodonzia" — pazienti senza assicurazione
    pipelineId: "YNar5ZkxXDvFp31UMmHY",
    stageId: "5cddb45b-4c2c-40e8-8bf8-a0e0499d1d5b", // "New Lead"
  },
  promemoria_ortodonzia: {
    // New pipeline "Promemoria - Ortodonzia 01" (May 2026 migration)
    pipelineId: "l191W5Qj1vs4QSEc9xVd",
    stageId: "",
    needsStageSelection: true,
  },
};

// Each Piano di Cura "mode" exposes a different set of reparti in the
// dropdown. The first key is the implicit default the user sees first.
// Labels are kept aligned with the actual GHL pipeline names so the staff
// recognises the same naming used inside GoHighLevel.
export const PIANO_CURA_MODES = {
  clinico: {
    title: "Piano di Cura — Reparto Clinico",
    defaultReparto: "reparto_clinico",
    options: [
      { value: "reparto_clinico", label: "Assicurazioni - Reparto Clinico" },
      { value: "reparto_clinico_completati", label: "Assicurazioni - Caricati da paziente (Generali Welion - Unisalute - Fasdac - Onenet-Aon)" },
      { value: "indiretti_clinico", label: "Indiretti - Reparto Clinico" },
      { value: "promemoria_appuntamento", label: "Promemoria Appuntamento" },
    ],
  },
  ortodonzia: {
    title: "Piano di Cura — Ortodonzia",
    defaultReparto: "ortodonzia",
    options: [
      { value: "ortodonzia", label: "Assicurazioni - Ortodonzia" },
      { value: "indiretti_ortodonzia", label: "Indiretti - Ortodonzia" },
      { value: "promemoria_ortodonzia", label: "Promemoria - Ortodonzia" },
    ],
  },
};

// Stage in cui un piano di cura è ancora modificabile dall'app. Appena
// l'opp passa allo stage successivo, l'app forza la sola lettura anche
// senza `?readonly=1` (e il backend rifiuta con 403 il PUT). Vale solo
// per le pipeline "Assicurazioni": Indiretti / Promemoria / legacy
// restano sempre modificabili. Mantieni allineato con il backend
// (server.py · EDITABLE_STAGE_NAMES_BY_PIPELINE) e con
// `frontend/src/pages/OpportunitaCreatePage.jsx`. NOTA: la verifica
// effettiva qui in App.js arriva dal backend via `data.is_editable` —
// le costanti sotto sono mantenute solo come riferimento documentale.
export const EDITABLE_STAGE_NAMES_BY_PIPELINE = {
  "529iRmd3Wh7bjdgfHpP9": new Set([
    "da autorizzare", "caricati", "caricati da paziente",
  ]),
  "Eu6Xz0GLvHfN5PejJ4SV": new Set([
    "preventivi da caricare", "preventivi caricati", "caricati da paziente",
  ]),
};
